import React, { useContext, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';
import Filter from 'app/src/elements/Filter';
import { ResetSettings } from 'app/src/elements/Pagination/ResetSettings';

import DataState from 'app/src/components/DataState';
import AuthContext from 'app/src/contexts/AuthContext';
import {
  MainContainer,
  ContentContainer,
  PageTitle,
  LinkContainer,
  LinkSpace,
  Button,
} from 'app/src/elements';
import Modal from 'app/src/elements/Modal';

import {
  BANK_ACCOUNTS,
  BANK_ACCOUNTS_WITH_COUNT,
  HIDE_BANK_ACCOUNT
} from './queries';
import SubNav from './SubNav';
import NewBankAccount from './new';
import EditBankAccount from './edit';

const checkedColumn = ({ value }) => value ? '✓' : '';

const inputStyle = {
  marginBottom: '10px'
}

const baseColumns = [
  {
    key: 'accountName',
    label: 'Account Name',
    entry: ({ entry }) => entry.accountName,
    sort: 'asc',
  },
  {
    key: 'bankName',
    label: 'Bank Name',
    value: ({ entry }) => entry.bankName,
    sort: 'asc',
  },
  {
    key: 'type',
    label: 'Type',
    value: ({ entry }) => entry?.type,
  },
  {
    key: 'routingNumberMasked',
    label: 'Routing Number',
    value: ({ entry }) => entry?.routingNumberMasked,
    sort: 'prevent'
  },
  {
    key: 'accountNumberMasked',
    label: 'Account Number',
    value: ({ entry }) => entry?.accountNumberMasked,
    sort: 'prevent'
  }
];

const BankAccounts = ({ ownerType, ownerId }) => {
  const [showAddModal, setShowAddModal] = useState();
  const [showEditModal, setShowEditModal] = useState();
  const { hasPolicy } = useContext(AuthContext);

  const [refetch, setRefetch] = useState();
  const [filterOptions, setFilterOptions] = useState();
  const [filters, setFilters] = useState({});
  const [settings, setSettings] = useState({});
  const queryVariables = {
    ownerType: ownerType,
    ownerId: ownerId,
    filterColumns: [],
    filterNames: [],
    scopes: [],
  };

  const setFilter = (key, value) => {
    const currentFilters = {...filters};
    currentFilters[key] = value;
    setFilters(currentFilters);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  // const { data } = useQuery(BANK_ACCOUNTS_WITH_COUNT,
  //   { variables: queryVariables }
  // );

  const [hideBankAccount] = useMutation(HIDE_BANK_ACCOUNT, { onCompleted: refetch });

  // if (!data) { return <DataState.Loading /> };

  const onHide = bankAccount => {
    if (window.confirm(`Hide ${bankAccount.accountName}?`)) {
      hideBankAccount({ variables: { id: bankAccount.id }});
    }
  };

  const columns = [
    ...baseColumns,
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      content: ({ entry }) => (
        <LinkContainer>
          <Pressable onPress={() => setShowEditModal(entry)}>
            <Text>Edit</Text>
          </Pressable>
          <LinkSpace />
          {hasPolicy({ type: 'BankAccount', action: 'hide' }) && (
            <Pressable onPress={() => onHide(entry)} >
              <Text>{entry.hiddenAt ? 'UnHide' : 'Hide'}</Text>
            </Pressable>
          )}
        </LinkContainer>
      )
    },
  ];

  const buttons = (
    <View style={{display: 'flex', flexDirection: 'row'}}>
      <Filter
        clearFilters={() => {
          setFilters({})
          setShowModal(false)
        }}
        filters={filters}
        setFilters={setFilters}
      >
        {/* <Text>Show Inactive?</Text>
        <input
          type={'checkbox'}
          name={'showHidden'}
          onChange={e => {
            setFilter('showHidden', e.target.checked)
          }}
          checked={filters.showHidden || false}
          style={{
            marginTop: '-14px',
            marginLeft: '-80px',
            zIndex: 1000,
            ...inputStyle
          }}
        /> */}
        <View style={{padding: '20px'}}>
          <Text>Bank Name:</Text>
          <input
            type={'text'}
            name="bank_name"
            onChange={e => setFilter('bank_name', e.target.value)}
            value={filters.bank_name || ''}
          />
          <Text>Account Name:</Text>
          <input
            type={'text'}
            name="account_name"
            onChange={e => setFilter('account_name', e.target.value)}
            value={filters.account_name || ''}
          />
          <Text>Bank Account Type</Text>
          <select
            type={'select'}
            name="account_group"
            onChange={e => {
              setFilter('type', e.target.value)}
            }
            value={filters.type || ''}
          >
            <option key={`bank-account-type-any`}>{'Any'}</option>
            {
              ['checking', 'savings'].map(type => (
              <option key={`bank-account-type-${type}`} value={type}>
                {type}
              </option>
            ))}
          </select>
        </View>
      </Filter>
      <ResetSettings />
    </View>
  );

  return (
    <MainContainer>
      {/* <SubNav ownerType={ownerType} ownerId={ownerId} refetch={refetch} selects={data} /> */}

      <ContentContainer>
        <PageTitle>Bank Accounts</PageTitle>
        <Pressable
          onPress={() => setShowAddModal(true)}
        >
          <Text style={{marginTop: '10px'}}>
            +Add Bank Account
          </Text>
        </Pressable>
        <DataTable.Gql
          setRefetch={setRefetch}
          query={BANK_ACCOUNTS_WITH_COUNT}
          queryName='bankAccountsWithCount'
          queryVariables={queryVariables}
          columns={columns}
          pageLength={25}
          buttons={buttons}
          lengthMenu={ [10, 25, 50, 'All'] }
          setSettings={setSettings}
          dataUpdated={data => setFilterOptions(data?.bankAccountsWithCount)}
          htmlTable
        />
      </ContentContainer>

      {showAddModal &&
        <Modal
          showModal
          setShowModal={setShowAddModal}
          modalType="Add Bank Account"
        >
          <NewBankAccount
            ownerType={ownerType}
            ownerId={ownerId}
            setShowModal={setShowAddModal}
            refetch={refetch}
          />
        </Modal>
      }

      {showEditModal &&
        <Modal
          showModal
          setShowModal={setShowEditModal}
          modalType="Edit Bank Account"
        >
          <EditBankAccount
            bankAccount={showEditModal}
            setShowModal={setShowEditModal}
            refetch={refetch}
            // selects={data}
          />
        </Modal>
      }
    </MainContainer>
  );
};

export default BankAccounts;
